/** @file One brush stroke: rows copied at pointer-down, spaced dabs, one commit on release. */
import { meshNeighbors } from '@pose2d/commands/mesh/meshNeighbors';
import { sameWeights } from '@pose2d/commands/mesh/sameWeights';
import type { Doc, Id } from '@pose2d/format/types';
import { invariant } from '@pose2d/std/invariant';
import type { MeshScene } from '../editMesh/meshScene';
import type { MeshTarget } from '../editMesh/meshTarget';
import { paintDab, type BrushSettings, type PaintSurface } from './paintDab';
import { strokeDabs } from './strokeDabs';

type Influences = [Id, number][];

/** A stroke in progress (world units; positions frozen at pointer-down so the live preview never feeds back). */
export interface PaintStroke {
  attachment: Id;
  bone: Id;
  start: readonly Influences[];
  rows: Influences[];
  surface: PaintSurface;
  radius: number;
  x: number;
  y: number;
  carry: number;
}

/** Dabs are this fraction of the radius apart. */
const SPACING = 0.25;

/** Begin (first dab), extend along the pointer path, and the bone a brush paints. */
export const paintStroke = {
  /** The bone painted: the brush's bone when it still exists, else the edited slot's bone. */
  bone(doc: Doc, target: MeshTarget, chosen: Id | null): Id {
    if (chosen !== null && doc.bones[chosen] !== undefined) return chosen;
    const slotBone = doc.slots[target.slot]?.bone;
    invariant(slotBone !== undefined, 'paintStroke.bone: the target slot is missing');
    return slotBone;
  },
  /** A stroke on `rows` (the mesh's current weights) with its first dab at world (x, y). */
  begin(s: MeshScene, rows: Influences[], bone: Id, brush: BrushSettings, at: { x: number; y: number; radius: number }): PaintStroke {
    const surface: PaintSurface = { points: s.points, neighbors: meshNeighbors(rows.length, s.target.mesh.triangles) };
    const stroke: PaintStroke = { attachment: s.target.attachment, bone, start: rows.map((r) => [...r]), rows, surface, radius: at.radius, x: at.x, y: at.y, carry: 0 };
    paintDab(stroke.rows, surface, at, bone, brush);
    return stroke;
  },
  /** Dabs from the last pointer position to world (x, y); true when any vertex changed. */
  extend(stroke: PaintStroke, x: number, y: number, brush: BrushSettings): boolean {
    const dabs = strokeDabs(stroke.x, stroke.y, x, y, stroke.radius * SPACING, stroke.carry);
    let touched = 0;
    for (let i = 0; i + 1 < dabs.centres.length; i += 2) {
      touched += paintDab(stroke.rows, stroke.surface, { x: dabs.centres[i] ?? x, y: dabs.centres[i + 1] ?? y, radius: stroke.radius }, stroke.bone, brush);
    }
    stroke.x = x;
    stroke.y = y;
    stroke.carry = dabs.carry;
    return touched > 0;
  },
  /** True when the stroke's rows differ from the weights at pointer-down. */
  changed(stroke: PaintStroke): boolean {
    return !sameWeights(stroke.start, stroke.rows);
  },
};
